import axios from '@/lib/axios'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { omit } from 'lodash'
import { ToastInterface, useToast } from 'vue-toastification'

import { HotelRate } from '@/interfaces/hotel'

interface Props {
	immediate?: boolean
	params?: Params
	config?: Config
}

interface Config {
	errorHandling: boolean
}

interface Params {
	id: string | number
}

interface UpdateParams {
	rateId: string | number
	typeCode: string
	amount: string
	agreement: boolean
	currency: string
	currencyConversion: string
	startDate: string
	expirationDate: string
}

interface StatusParams {
	id: string | number
	commentary: string
}

const getRate = async (params: Params) => {
	const { data } = await axios.get<HotelRate>(
		`/api/internal/rates/${params.id}`
	)

	return data
}

const updateRate = async (rate: UpdateParams, toast: ToastInterface) => {
	try {
		const { data } = await axios.put<HotelRate>(
			`/api/internal/rates/${rate.rateId}`,
			omit(rate, 'rateId')
		)
		toast.success('Se actualizó correctamente la tarifa del hotel')

		return data
	} catch (error: any) {
		toast.error(error.response.data.message)
	}
}

const deleteRate = async (toast: ToastInterface, params: StatusParams) => {
	try {
		await axios.delete(`/api/internal/rates/${params.id}`, {
			data: {
				comment: params.commentary,
			},
		})
		toast.success('Se eliminó correctamente la tarifa del hotel')
	} catch (error: any) {
		toast.error(error.response.data.message)
	}
}

const reactiveRate = async (toast: ToastInterface, params: StatusParams) => {
	try {
		await axios.post(`/api/internal/rates/${params.id}/restore`, {
			comment: params.commentary,
		})
		toast.success('Se reactivó correctamente la tarifa del hotel')
	} catch (error: any) {
		toast.error(error.response.data.message)
	}
}

const useRate = (props: Props) => {
	const toast = useToast()

	const { data, isLoading, refetch } = useQuery(
		['rate', props.params?.id],
		() => getRate(props.params!),
		{
			enabled: !!props.params?.id,
		}
	)

	const updateRateMutation = useMutation((rate: UpdateParams) =>
		updateRate(rate, toast)
	)

	const deleteRateMutation = useMutation((rate: StatusParams) =>
		deleteRate(toast, rate)
	)

	const reactiveRateMutation = useMutation((rate: StatusParams) =>
		reactiveRate(toast, rate)
	)

	return {
		data,
		isLoading,
		refetch,

		updateRate: updateRateMutation.mutate,
		updateRateAsync: updateRateMutation.mutateAsync,
		updateRateIsLoading: updateRateMutation.isLoading,

		deleteRate: deleteRateMutation.mutate,
		deleteRateAsync: deleteRateMutation.mutateAsync,
		deleteRateIsLoading: deleteRateMutation.isLoading,

		reactiveRate: reactiveRateMutation.mutate,
		reactiveRateAsync: reactiveRateMutation.mutateAsync,
		reactiveRateIsLoading: reactiveRateMutation.isLoading,
	}
}

export default useRate
